'use client';

import { useCallback, useRef } from 'react';

/**
 * Tracks whether the Ozonetel softphone is registered and can carry an outbound call.
 *
 * `initialize` resolves once the agent is logged in, but the softphone registers a moment later and
 * Ozonetel discards a dial issued in that gap without reporting an error. The SDK bridges its JsSIP
 * transport events onto `onSIPRegistered` / `onSIPDisconnected` / `onSIPRegistrationFailed`, which
 * is what moves this flag — and what the `ozt_onSipRegistered` / `ozt_onSipDisconnected` page events
 * carry, so interfaces can gate their call controls on the same signal.
 */
export function useOzonetelSipReadiness() {
  const isSipReadyRef = useRef(false);

  const markSipReady = useCallback(() => {
    isSipReadyRef.current = true;
  }, []);

  const markSipLost = useCallback(() => {
    isSipReadyRef.current = false;
  }, []);

  const assertSipReady = useCallback(() => {
    if (isSipReadyRef.current) return;

    throw new Error(
      'Cannot start call: the Ozonetel softphone is not registered yet. Gate the call action on the On Agent Ready page event. Resolve this by ensuring that call is placed only after `onSipRegistered` event has been triggered',
    );
  }, []);

  return { markSipReady, markSipLost, assertSipReady } as const;
}

export type OzonetelSipReadiness = ReturnType<typeof useOzonetelSipReadiness>;
